import React, { useState, useEffect, useCallback } from 'react';
import MultiSelect from './MultiSelect';
import './FilterBar.css';

const DATE_RANGE_OPTIONS = [
  { value: 1, label: 'Last 24 hours' },
  { value: 2, label: 'Last 2 days' },
  { value: 3, label: 'Last 3 days' },
  { value: 7, label: 'Last 7 days' },
  { value: 14, label: 'Last 14 days' },
  { value: 30, label: 'Last 30 days' },
  { value: 90, label: 'Last 90 days' },
  { value: 365, label: 'Last year' },
  { value: 0, label: 'All time' },
  { value: 'custom', label: 'Custom range' },
];

/**
 * Default filter state for performance filters.
 * Used by App.js to initialize lifted state.
 */
export const DEFAULT_PERFORMANCE_FILTERS = {
  daysFilter: 7,
  customStartDate: '',
  customEndDate: '',
  selectedDevices: [],
  selectedQualities: [],
  selectedBranches: ['current_dev'],
  selectedCLs: [],
  selectedTests: [],
};

/**
 * Shared filter bar for performance tabs.
 * Queries the captures table for available filter values.
 *
 * @param {Object} props
 * @param {Object} props.api - HarvestTimeAPI instance
 * @param {Object} props.filters - Current filter state (lifted to App.js)
 * @param {Function} props.onFiltersChange - setState updater for filters
 * @param {Function} props.onResolvedFilters - Callback with resolved { effectiveDays, startDate, endDate, filters }
 * @param {boolean} props.showTestFilter - Whether to show the test name filter (default true)
 * @param {React.ReactNode} props.extraFilters - Additional filter elements to render in the second row
 */
function FilterBar({ api, filters, onFiltersChange, onResolvedFilters, showTestFilter = true, extraFilters }) {
  const {
    daysFilter, customStartDate, customEndDate,
    selectedDevices, selectedQualities, selectedBranches, selectedCLs, selectedTests,
  } = filters;

  const startDate = daysFilter === 'custom' && customStartDate ? customStartDate : null;
  const endDate = daysFilter === 'custom' && customEndDate ? customEndDate : null;
  const effectiveDays = daysFilter === 'custom' ? 0 : daysFilter;

  const updateFilter = useCallback((key, value) => {
    onFiltersChange(prev => ({ ...prev, [key]: value }));
  }, [onFiltersChange]);

  // Available filter options fetched from server
  const [filterOptions, setFilterOptions] = useState({
    devices: [], qualities: [], branches: [], cls: [], tests: [],
  });

  // Fetch filter options from captures table
  useEffect(() => {
    if (daysFilter === 'custom' && !customStartDate) return;
    let cancelled = false;

    async function fetchFilters() {
      try {
        const result = await api.getPerformanceFilters(effectiveDays, startDate, endDate);
        if (!cancelled) {
          setFilterOptions({
            devices: result.devices || [],
            qualities: result.qualities || [],
            branches: result.branches || [],
            cls: result.cls || [],
            tests: result.tests || [],
          });
        }
      } catch (err) {
        console.warn('Failed to load performance filter options:', err.message);
      }
    }

    fetchFilters();
    return () => { cancelled = true; };
  }, [api, effectiveDays, startDate, endDate, daysFilter, customStartDate]);

  // Notify parent of resolved filter values whenever they change
  useEffect(() => {
    if (onResolvedFilters) {
      onResolvedFilters({
        effectiveDays,
        startDate,
        endDate,
        filters: {
          devices: selectedDevices,
          qualities: selectedQualities,
          branches: selectedBranches,
          cls: selectedCLs,
          tests: selectedTests,
        },
      });
    }
  }, [onResolvedFilters, effectiveDays, startDate, endDate,
      selectedDevices, selectedQualities, selectedBranches, selectedCLs, selectedTests]);

  return (
    <div className="filter-bar">
      <div className="filter-bar-row">
        <div className="filter-bar-item">
          <label htmlFor="perf-date-range">Date range:</label>
          <select
            id="perf-date-range"
            value={daysFilter}
            onChange={(e) => {
              const val = e.target.value;
              updateFilter('daysFilter', val === 'custom' ? 'custom' : Number(val));
            }}
          >
            {DATE_RANGE_OPTIONS.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
          {daysFilter === 'custom' && (
            <div className="custom-date-inputs">
              <input
                type="date"
                value={customStartDate}
                onChange={(e) => updateFilter('customStartDate', e.target.value)}
              />
              <span className="date-separator">to</span>
              <input
                type="date"
                value={customEndDate}
                onChange={(e) => updateFilter('customEndDate', e.target.value)}
              />
            </div>
          )}
        </div>
      </div>
      <div className="filter-bar-row">
        <MultiSelect
          label="Devices:"
          options={filterOptions.devices}
          selected={selectedDevices}
          onChange={(v) => updateFilter('selectedDevices', v)}
          placeholder="All devices"
        />
        <MultiSelect
          label="Quality:"
          options={filterOptions.qualities}
          selected={selectedQualities}
          onChange={(v) => updateFilter('selectedQualities', v)}
          placeholder="All qualities"
        />
        <MultiSelect
          label="Branch:"
          options={filterOptions.branches}
          selected={selectedBranches}
          onChange={(v) => updateFilter('selectedBranches', v)}
          placeholder="All branches"
        />
        <MultiSelect
          label="CL:"
          options={filterOptions.cls}
          selected={selectedCLs}
          onChange={(v) => updateFilter('selectedCLs', v)}
          placeholder="All CLs"
        />
        {showTestFilter && (
          <MultiSelect
            label="Test:"
            options={filterOptions.tests}
            selected={selectedTests}
            onChange={(v) => updateFilter('selectedTests', v)}
            placeholder="All tests"
          />
        )}
        {extraFilters}
      </div>
    </div>
  );
}

export default FilterBar;
